import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";

export type AuctionsBrowseMode = "all" | "foryou";

type AuctionsBrowseTabsProps = {
  mode: AuctionsBrowseMode;
  onModeChange: (mode: AuctionsBrowseMode) => void;
  allLabel: string;
  forYouLabel: string;
  /** Light style for use above white browse content */
  variant?: "onDark" | "onLight";
  className?: string;
};

export function AuctionsBrowseTabs({
  mode,
  onModeChange,
  allLabel,
  forYouLabel,
  variant = "onLight",
  className,
}: AuctionsBrowseTabsProps) {
  const onDark = variant === "onDark";
  const { language } = useLanguage();
  const newBadge = auctionsLabels(language).browse.new;

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-5",
        className,
      )}
      role="tablist"
      aria-label="Browse mode"
    >
      {(
        [
          { id: "all" as const, label: allLabel },
          { id: "foryou" as const, label: forYouLabel },
        ] as const
      ).map((tab) => {
        const on = mode === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onModeChange(tab.id)}
            className={cn(
              "relative inline-flex items-center gap-1.5 px-1 py-1.5 text-[15px] font-semibold leading-none tracking-tight transition-colors duration-300",
              onDark
                ? on
                  ? "text-white"
                  : "text-white/60 hover:text-white/85"
                : on
                  ? "text-foreground"
                  : // Stronger inactive contrast on pale För dig / browse headers
                    "text-foreground/55 hover:text-foreground/80",
            )}
          >
            <span className="leading-none">{tab.label}</span>
            {tab.id === "foryou" && (
              <span className="inline-flex h-[16px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[8px] font-bold uppercase leading-none tracking-wide text-white">
                {newBadge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
